import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert,
  TextField, Button, FormControlLabel, Checkbox, Grid
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import useApi from '../utils/useApi';
import global from '../config/Global.json';

const MigrationLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ file_name: '', date_from: '', date_to: '', has_errors: false, called_by: '', api_name: '' });
  const { Get } = useApi();

  const fetchLogs = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      if (params.file_name) query.append('file_name', params.file_name);
      if (params.date_from) query.append('date_from', params.date_from);
      if (params.date_to) query.append('date_to', params.date_to);
      if (params.has_errors) query.append('has_errors', 'true');
      if (params.called_by) query.append('called_by', params.called_by);
      if (params.api_name) query.append('api_name', params.api_name);

      const url = `${global.api.host}${global.api.migrationLogs}${query.toString() ? '?' + query.toString() : ''}`;
      const token = JSON.parse(localStorage.getItem('authToken'))?.access;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setLogs(data);
    } catch {
      setError('Failed to load migration logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = () => fetchLogs(filters);

  const handleClear = () => {
    const reset = { file_name: '', date_from: '', date_to: '', has_errors: false, called_by: '', api_name: '' };
    setFilters(reset);
    fetchLogs(reset);
  };

  return (
    <Container maxWidth="xl" style={{ marginTop: '120px' }}>
      <Box py={4}>
        <Typography variant="h4" gutterBottom>Migration Logs</Typography>

        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>API Name</InputLabel>
                <Select value={filters.api_name} label="API Name"
                  onChange={e => setFilters(p => ({ ...p, api_name: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="migrateGroups">migrateGroups</MenuItem>
                  <MenuItem value="migrateDrvGroups">migrateDrvGroups</MenuItem>
                  <MenuItem value="migrateUsers">migrateUsers</MenuItem>
                  <MenuItem value="migrateSubscriptions">migrateSubscriptions</MenuItem>
                  <MenuItem value="migrateTransactions">migrateTransactions</MenuItem>
                  <MenuItem value="createTransactionsFromCSV">createTransactionsFromCSV</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth size="small" label="File Name" value={filters.file_name}
                onChange={e => setFilters(p => ({ ...p, file_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth size="small" label="Date From" type="datetime-local" InputLabelProps={{ shrink: true }}
                value={filters.date_from} onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth size="small" label="Date To" type="datetime-local" InputLabelProps={{ shrink: true }}
                value={filters.date_to} onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth size="small" label="Called By (phone)" value={filters.called_by}
                onChange={e => setFilters(p => ({ ...p, called_by: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControlLabel control={
                <Checkbox checked={filters.has_errors} onChange={e => setFilters(p => ({ ...p, has_errors: e.target.checked }))} />
              } label="Has Errors" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Box display="flex" gap={1}>
                <Button variant="contained" startIcon={<Search />} onClick={handleSearch}>Search</Button>
                <Button variant="outlined" startIcon={<Clear />} onClick={handleClear}>Clear</Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && logs.length === 0 && <Alert severity="info">No migration logs found.</Alert>}

        {!loading && logs.map((log) => (
          <Paper key={log.id} elevation={2} sx={{ mb: 3, p: 2 }}>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={1}>
              <Chip label={log.api_name} color="primary" size="small" />
              <Chip label={`Created: ${log.created_count}`} color="success" size="small" />
              {log.date_error_count > 0 && <Chip label={`Date Errors: ${log.date_error_count}`} color="error" size="small" />}
              <Typography variant="caption" color="text.secondary">{new Date(log.called_at).toLocaleString()}</Typography>
              {log.called_by && <Typography variant="caption" color="text.secondary">by {log.called_by}</Typography>}
              {log.file_name && <Typography variant="caption" color="text.secondary">File: {log.file_name}</Typography>}
            </Box>

            {log.migrated_records?.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>Migrated Records ({log.migrated_records.length})</Typography>
                <TableContainer sx={{ maxHeight: 250, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {['Row', 'Group', 'Subscription', 'Amount', 'Date', 'Description'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: '#fff' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {log.migrated_records.map((r, i) => (
                        <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                          <TableCell>{r.row}</TableCell>
                          <TableCell>{r.group}</TableCell>
                          <TableCell>{r.subscription}</TableCell>
                          <TableCell>{r.amount}</TableCell>
                          <TableCell>{r.date}</TableCell>
                          <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {log.date_errors?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="error" gutterBottom>Date Errors ({log.date_errors.length})</Typography>
                <TableContainer sx={{ border: '1px solid #ffcdd2', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Row', 'Group', 'Subscription', 'Bad Value', 'Error'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 'bold', bgcolor: '#d32f2f', color: '#fff' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {log.date_errors.map((e, i) => (
                        <TableRow key={i} sx={{ bgcolor: '#fff8f8' }}>
                          <TableCell>{e.row}</TableCell>
                          <TableCell>{e.group}</TableCell>
                          <TableCell>{e.subscription}</TableCell>
                          <TableCell><code>{e.tranat}</code></TableCell>
                          <TableCell sx={{ color: '#d32f2f' }}>{e.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    </Container>
  );
};

export default MigrationLogsPage;
